
import { useTranslation } from "react-i18next";

import { Dialog, DialogContent, DialogHeader } from "../ui/dialog";
import { Progress } from "../ui/progress";
import { Forfait } from "@/lib/types";
import { CircleCheckBig, Crown } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { useState } from "react";
import { useGetOptions, useSubscribeOption } from "@/api/option";
import { QuestionMarkCircledIcon } from "@radix-ui/react-icons";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { useUser } from "@/api/auth";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useEffect } from "react";

type ShowOptionsDialogProps = {
  open: boolean;
  forfait: Forfait;
  onOpenChange: (open: boolean) => void;
  setSelectedOptionName: (name: string) => void;
};

const ShowOptionsDialog = ({ onOpenChange, open, forfait, setSelectedOptionName }: ShowOptionsDialogProps) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<string>("");
  const { data: options } = useGetOptions();
  const auth = useAuth();
  const { data: user } = useUser(auth.token);
  const [totalWeight, setTotalWeight] = useState<number>(0);
  const { mutate: subscribeOption } = useSubscribeOption();

  useEffect(() => {
    if (forfait?.subscribedOptions?.length > 0) {
      setSelectedOption(forfait?.subscribedOptions?.filter((option) => option.type === "HD")?.at(0)?.id ?? "");
    }
  }, [forfait]);

  useEffect(() => {
    let weight = forfait?.maxWeight ?? 0
    if (forfait?.subscribedOptions?.length ?? 0 > 0) {
      const option = forfait?.subscribedOptions.find((option) => option.type === 'HD');
      if (option) {
        weight += option.maxWeight ?? 0;
      }
    }
    setTotalWeight(weight);
  }, [forfait]);

  const getSpaceSpace = () => {
    if (forfait?.usedSpaceInGB && totalWeight) {
      return (forfait?.usedSpaceInGB / totalWeight) * 100 > 100 ? 100 : (forfait?.usedSpaceInGB / totalWeight) * 100;
    }
    return 0;
  };

  const subscribeToOption = () => {
    try {
      if (selectedOption && user?.at(0)?.id) {
        subscribeOption({ optionId: selectedOption, userId: user.at(0).id });
        onOpenChange(false);
        setSelectedOptionName(options?.find((option) => option.id === selectedOption)?.name ?? "");
      }
    } catch (error) {
      console.error(error);
      toast({
        variant: "error",
        description: t("toastOptionAjoutErreurDescription"),
      });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <div className="flex justify-center items-center font-bold mt-4 text-lg">
              {t("showOptions")}
            </div>
          </DialogHeader>
          <div className="flex flex-row gap-2 px-16 mx-12">
            <div className="mx-8 w-3xs bg-[#F9FAFB] rounded-xl p-4 h-full">
              <div className="font-bold mb-2">
                {t("yourAccountInformation")}
              </div>
              <div className="flex flex-col gap-2 ">
                <div className="flex flex-col gap-2 w-full">
                  <div className="flex flex-col">
                    <p className="text-gray-900 text-[16px] font-[500] font-[Raleway]">
                      {t("mainSubStockageWeigth")}
                    </p>
                    <p className="text-gray-600 text-[16px] font-[500] font-[Raleway]">
                      {forfait?.usedSpaceInGB} / {totalWeight} Go
                    </p>
                  </div>
                  <Progress value={getSpaceSpace()} className="w-full" color={getSpaceSpace() < 70 ? 'primary' : getSpaceSpace() < 90 ? 'amber-500' : 'red-500'} />
                </div>
              </div>
              <div className="flex flex-row gap-2 items-center mt-2">
                <CircleCheckBig className="w-4 h-4 text-primary" />
                {totalWeight} Go
              </div>
              <div className="flex flex-row gap-2 items-center mt-2">
                <CircleCheckBig className="w-4 h-4 text-primary" />
                {forfait.maxArtworks} fiches
              </div>
              <div className="flex flex-row gap-2 items-center mt-2">
                <CircleCheckBig className="w-4 h-4 text-primary" />
                {forfait.maxUsers} utilisateurs
              </div>
            </div>
            <div className="flex-1 w-sm">
              <div className="flex flex-col border border-[#E1EBF7] rounded-xl p-4">
                <div className="font-bold mb-2">
                  {t("showOptions")}
                </div>
                <RadioGroup
                  value={selectedOption}
                  onValueChange={(value) => {
                    setSelectedOption(value);
                  }}
                  className="flex flex-col gap-4"
                >
                  {options?.map((option) => (
                    <div key={option.id} className={`flex items-center justify-between mx-4 rounded-xl pr-4 ${selectedOption === option.id ? "bg-hdOption-bg" : "bg-[#F9FAFB]"}`}>
                      <Label htmlFor="shared" className="text-xs">
                        <div className="flex my-4 items-center justify-between ">
                          <Crown className="w-4 h-4 mx-4 text-hdOption-icon" />
                          <div className={`mr-2 text-hdOption-text ${selectedOption === option.id ? "font-bold" : ""}`}>
                            {option.name}
                          </div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <QuestionMarkCircledIcon className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-[200px] text-center text-[#6F6C90]">
                                  <p>{option.tooltip}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                        </div>
                      </Label>
                      <RadioGroupItem value={option.id} id={option.id} />
                    </div>
                  ))}
                </RadioGroup>
              </div>
              <Button disabled={selectedOption === ""} className="w-full mt-4" onClick={subscribeToOption}>
                {t("SubscribeNewOption")}
              </Button>
            </div>  
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShowOptionsDialog;
